import { useCallback, useEffect, useState } from 'react';

import {
  LoadingSuspense,
  Paragraph,
  SubtitleSecondary,
} from '@components/index';
import labels from '@config/labels';
import { apiRebox } from '@services/index';

import {
  IPropsDashboards,
  IStatusContract,
  selectDashboardDateTypes,
} from '../../TypesOfDashboards/typings';
import HeaderMobileOrDesktopDashboard from '../HeaderMobileOrDesktopDashboard';

import {
  Card,
  Container,
  Info,
  InformationHorizontally,
  InformationVertically,
} from './styles';

interface IProps extends React.HTMLAttributes<HTMLDivElement> {
  period_start: string;
  period_end: string;
  IpropsDashboards: IPropsDashboards;
  dataOverview: {
    tpa: number;
    tpat: number;
    tpag: number;
    loadingOverview: boolean;
  };
}

interface IDashboardSummaryByProduct {
  name: string;
  count: number;
}

interface IDashboardSummaryByEnvironment {
  environment: string;
  count: number;
}

interface IDashboardSummary {
  contracts: {
    total: number;
    sum_amount: number;
    by_status: Record<string, number>;
    by_product: IDashboardSummaryByProduct[];
    by_environment: IDashboardSummaryByEnvironment[];
  };
  users: {
    lead_count: number;
  };
}

const CardDashboardCommercialDefault: React.FC<IProps> = ({
  period_start,
  period_end,
  dataOverview,
  IpropsDashboards: {
    dashboardDateTypesMobile,
    dashboardDateTypesDesktop,
    setLoadingDashboard,
    setFirstLoadingDashboard,
    firstLoadingDashboard,
  },
  ...rest
}) => {
  // Estado para formatação de dados
  const [
    dataDashboardCommercialDayFormatted,
    setDataDashboardCommercialDayFormatted,
  ] = useState({
    pv: 0,
    tkm: 0,
    lead: 0,
    rAuto: [
      {
        planName: '',
        total: 0,
        percent: 0,
      },
    ],
    scheduled: 0,
    madeEffective: 0,
    canceled: 0,
    channels: [
      {
        channelName: '',
        percent: 0,
      },
    ],
    city: [
      {
        cityName: '',
        stateName: '',
        total: 0,
        percent: 0,
      },
    ],
  });

  // Estado com o resumo já agregado pelo backend (GET /contracts/dashboard-summary)
  const [
    dataDashboardCommercial,
    setDataDashboardCommercial,
  ] = useState<IDashboardSummary>({
    contracts: {
      total: 0,
      sum_amount: 0,
      by_status: {},
      by_product: [],
      by_environment: [],
    },
    users: {
      lead_count: 0,
    },
  });

  // Funções de formatação de dados:
  const tkmFinal = useCallback(() => {
    const { total, sum_amount } = dataDashboardCommercial.contracts;
    if (total === 0) {
      return 0;
    }
    return Number((sum_amount / total).toFixed(2));
  }, [dataDashboardCommercial]);

  const calculatePlanPercentage = useCallback(() => {
    const { total, by_product } = dataDashboardCommercial.contracts;

    return by_product.map(product => {
      const percentage = total === 0 ? 0 : (product.count / total) * 100;
      return {
        productName: product.name,
        amountProduct: product.count,
        percentageToFixed: Number(percentage.toFixed(2)),
      };
    });
  }, [dataDashboardCommercial]);

  const plansAwaitingCanceledAndEffective = useCallback((): IStatusContract => {
    const { by_status } = dataDashboardCommercial.contracts;
    return {
      canceled: by_status.CANCELED || 0,
      released: by_status.RELEASED || 0,
      pending: by_status.PENDING || 0,
    };
  }, [dataDashboardCommercial]);

  const leadCustomerStatus = useCallback(() => {
    return dataDashboardCommercial.users.lead_count;
  }, [dataDashboardCommercial]);

  const formatterChannels = useCallback((environment: string) => {
    for (const element of labels.rebox.filter.contract.environment) {
      if (environment === element.value) {
        return element.label;
      }
    }
    return labels.rebox.filter.contract.environment[0].label;
  }, []);

  const calculateChannelsPercentage = useCallback(() => {
    const { total, by_environment } = dataDashboardCommercial.contracts;

    return by_environment.map(channel => {
      const percentage = total === 0 ? 0 : (channel.count / total) * 100;
      return {
        channelName: formatterChannels(channel.environment),
        percentageToFixed: Number(percentage.toFixed(2)),
      };
    });
  }, [dataDashboardCommercial, formatterChannels]);

  const dataFormatted = useCallback(() => {
    setDataDashboardCommercialDayFormatted({
      ...dataDashboardCommercialDayFormatted,
      pv: dataDashboardCommercial.contracts.total,
      tkm: tkmFinal(),
      lead: leadCustomerStatus(),
      rAuto: calculatePlanPercentage().map(product => {
        return {
          planName: product.productName,
          total: product.amountProduct,
          percent: product.percentageToFixed,
        };
      }),
      canceled: plansAwaitingCanceledAndEffective().canceled,
      madeEffective: plansAwaitingCanceledAndEffective().released,
      scheduled: plansAwaitingCanceledAndEffective().pending,
      channels: calculateChannelsPercentage().map(channel => {
        return {
          channelName: channel.channelName,
          percent: channel.percentageToFixed,
        };
      }),
      // Quebra por cidade ainda não é devolvida pelo /contracts/dashboard-summary (join adiado pro backend).
      city: [],
    });
  }, [dataDashboardCommercial]);

  const getDataDashboardCommercialDay = useCallback(async () => {
    try {
      setLoadingDashboard(true);

      const { data: response } = await apiRebox.get(
        `/contracts/dashboard-summary`,
        {
          params: {
            period_start,
            period_end,
          },
        },
      );

      setDataDashboardCommercial(response.data);
    } catch (error) {
      console.error(
        `Error ao tentar buscar dados para o dashboard comercial: Na data e hora ${period_start} - ${period_end}: `,
        error,
      );
    } finally {
      setLoadingDashboard(false);
      setFirstLoadingDashboard(false);
    }
  }, [dashboardDateTypesMobile]);

  useEffect(() => {
    getDataDashboardCommercialDay();
    const interval = setInterval(getDataDashboardCommercialDay, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [dashboardDateTypesMobile]);

  useEffect(() => {
    setFirstLoadingDashboard(true);
  }, []);

  useEffect(() => {
    dataFormatted();
  }, [dataDashboardCommercial]);

  return (
    <>
      {firstLoadingDashboard ? (
        <LoadingSuspense />
      ) : (
        <Container {...rest}>
          {dashboardDateTypesDesktop ? (
            <HeaderMobileOrDesktopDashboard
              dashboardDateTypes={
                dashboardDateTypesDesktop as selectDashboardDateTypes
              }
              period_end={period_end}
              period_start={period_start}
            />
          ) : (
            <HeaderMobileOrDesktopDashboard
              dashboardDateTypes={
                dashboardDateTypesMobile as selectDashboardDateTypes
              }
              period_end={period_end}
              period_start={period_start}
            />
          )}
          <Card>
            <InformationHorizontally>
              <Paragraph nameColor="black" fontSize={16} fontWeight={500}>
                P.V:
                <Info>
                  {dataDashboardCommercialDayFormatted.pv === 0
                    ? 'Não teve vendas'
                    : dataDashboardCommercialDayFormatted.pv}
                </Info>
              </Paragraph>
              <Paragraph nameColor="black" fontSize={16} fontWeight={500}>
                TKM:
                <Info>
                  {dataDashboardCommercialDayFormatted.pv === 0
                    ? 'Não teve vendas'
                    : `R$ ${dataDashboardCommercialDayFormatted.tkm}`}
                </Info>
              </Paragraph>
              <Paragraph nameColor="black" fontSize={16} fontWeight={500}>
                Lead:{' '}
                <Info>
                  {' '}
                  {dataDashboardCommercialDayFormatted.pv === 0
                    ? 'Não teve vendas'
                    : `${dataDashboardCommercialDayFormatted.lead}`}
                </Info>
              </Paragraph>
            </InformationHorizontally>
            <InformationVertically>
              <SubtitleSecondary textAlign="start" className="plans-contracts">
                Planos mais contratados
              </SubtitleSecondary>

              {dataDashboardCommercialDayFormatted.pv === 0 ? (
                <>
                  <Paragraph
                    nameColor="black"
                    fontSize={16}
                    fontWeight={500}
                    textAlign="start"
                  >
                    <Info>Não teve vendas</Info>
                  </Paragraph>
                </>
              ) : (
                <>
                  {dataDashboardCommercialDayFormatted.rAuto.map(
                    (plan, key) => (
                      <Paragraph
                        nameColor="black"
                        fontSize={16}
                        fontWeight={500}
                        textAlign="start"
                        key={`${plan.planName}-${key}`}
                      >
                        {plan.planName}: <Info>{plan.total}</Info>
                        {'|'}
                        <Info>{plan.percent}%</Info>
                      </Paragraph>
                    ),
                  )}
                </>
              )}
            </InformationVertically>
            <InformationHorizontally>
              <Paragraph nameColor="black" fontSize={16} fontWeight={500}>
                Ag:{' '}
                <Info>
                  {dataDashboardCommercialDayFormatted.pv === 0
                    ? 'Não teve vendas'
                    : dataDashboardCommercialDayFormatted.scheduled}
                </Info>
              </Paragraph>
              <Paragraph nameColor="black" fontSize={16} fontWeight={500}>
                Efet:{' '}
                <Info>
                  {' '}
                  {dataDashboardCommercialDayFormatted.pv === 0
                    ? 'Não teve vendas'
                    : dataDashboardCommercialDayFormatted.madeEffective}
                </Info>
              </Paragraph>
              <Paragraph nameColor="black" fontSize={16} fontWeight={500}>
                Can:{' '}
                <Info>
                  {' '}
                  {dataDashboardCommercialDayFormatted.pv === 0
                    ? 'Não teve vendas'
                    : dataDashboardCommercialDayFormatted.canceled}
                </Info>
              </Paragraph>
            </InformationHorizontally>
            <InformationVertically>
              <SubtitleSecondary textAlign="start">Canais</SubtitleSecondary>

              <InformationVertically>
                {dataDashboardCommercialDayFormatted.pv === 0 ? (
                  <>
                    <Paragraph
                      nameColor="black"
                      fontSize={16}
                      fontWeight={500}
                      textAlign="start"
                    >
                      <Info>Não teve vendas</Info>
                    </Paragraph>
                  </>
                ) : (
                  <>
                    {dataDashboardCommercialDayFormatted.channels.map(
                      (channel, key) => (
                        <Paragraph
                          nameColor="black"
                          fontSize={16}
                          fontWeight={500}
                          textAlign="start"
                          key={`${channel.channelName}-${key}`}
                        >
                          {channel.channelName}: <Info>{channel.percent}%</Info>
                        </Paragraph>
                      ),
                    )}
                  </>
                )}
              </InformationVertically>
            </InformationVertically>
            <InformationVertically>
              <SubtitleSecondary textAlign="start">Cidades</SubtitleSecondary>
              <InformationVertically>
                {dataDashboardCommercialDayFormatted.pv === 0 ? (
                  <>
                    <Paragraph
                      nameColor="black"
                      fontSize={16}
                      fontWeight={500}
                      textAlign="start"
                    >
                      <Info>Não teve vendas</Info>
                    </Paragraph>
                  </>
                ) : dataDashboardCommercialDayFormatted.city.length === 0 ? (
                  <Paragraph
                    nameColor="black"
                    fontSize={16}
                    fontWeight={500}
                    textAlign="start"
                  >
                    <Info>Indisponível no momento</Info>
                  </Paragraph>
                ) : (
                  <>
                    {dataDashboardCommercialDayFormatted.city.map(
                      (city, key) => (
                        <Paragraph
                          nameColor="black"
                          fontSize={16}
                          fontWeight={500}
                          textAlign="start"
                          key={`${city.cityName}-${key}`}
                        >
                          {`${city.cityName}(${city.stateName})`}:
                          <Info>{city.total}</Info> {'|'}
                          <Info>{city.percent}%</Info>
                        </Paragraph>
                      ),
                    )}
                  </>
                )}
              </InformationVertically>
            </InformationVertically>
            <InformationVertically>
              <SubtitleSecondary>Resumo Geral</SubtitleSecondary>
              <InformationHorizontally>
                {dataOverview.loadingOverview ? (
                  <Paragraph
                    nameColor="black"
                    fontSize={16}
                    fontWeight={500}
                    textAlign="center"
                  >
                    Carregando resumo geral...
                  </Paragraph>
                ) : (
                  <>
                    <Paragraph nameColor="black" fontSize={16} fontWeight={500}>
                      TPA:
                      <Info>{dataOverview.tpa}</Info>
                    </Paragraph>
                    <Paragraph nameColor="black" fontSize={16} fontWeight={500}>
                      TPAT:
                      <Info> {dataOverview.tpat}</Info>
                    </Paragraph>
                    <Paragraph nameColor="black" fontSize={16} fontWeight={500}>
                      TPAG:
                      <Info>{dataOverview.tpag}</Info>
                    </Paragraph>
                  </>
                )}
              </InformationHorizontally>
            </InformationVertically>
          </Card>
        </Container>
      )}
    </>
  );
};
export default CardDashboardCommercialDefault;
